/**
 * hug-arrow
 * ---------
 * Keeps a trailing arrow tucked in directly beside its label instead of being
 * stranded at a full-width row's right edge when the label wraps to two lines.
 *
 * Why JS is needed: CSS has no way to size a box to "the widest line of
 * naturally-wrapped text". We measure that width per element and pin the label
 * to it, so the arrow (a flex sibling) sits beside the text, vertically centered.
 *
 * Dynamic content: elements are (re-)scanned on every refresh and via a
 * MutationObserver on the root, so nodes injected after construction — e.g. the
 * cards a `<events-overview>`/`<publications-overview>` fetches client-side —
 * get wrapped and measured too. Each element is wrapped at most once.
 *
 * Requirements on the target elements:
 *   1. The element is a flex container (e.g. `display:flex; align-items:center`).
 *   2. The arrow is a CSS `::after` pseudo-element on the element.
 *   3. That arrow does NOT shrink — give it `flex-shrink: 0` (Tailwind `shrink-0`),
 *      otherwise it collapses during measurement and renders undersized.
 *
 * Example:
 *   import hugArrow from '@helpers/hug-arrow'
 *
 *   const arrows = hugArrow('.with-arrow')
 *   // ...later, when the element becomes visible (e.g. a menu opens):
 *   arrows.refresh()
 *   // ...on teardown:
 *   arrows.destroy()
 */

export interface HugArrowController {
    /** Re-measure and re-apply. Call after the elements become visible or their text changes. */
    refresh(): void
    /** Remove the resize listener and MutationObserver. Applied inline styles are left as-is. */
    destroy(): void
}

export interface HugArrowOptions {
    /** Where to query for elements. Defaults to `document`. */
    root?: ParentNode
    /**
     * Gate for when the effect should apply. Return `false` (e.g. on desktop) and
     * the width override is cleared so CSS lays the elements out normally.
     * Defaults to always-on.
     */
    active?: () => boolean
}

export default function hugArrow(selector: string, options: HugArrowOptions = {}): HugArrowController {
    const { root = document, active = () => true } = options

    // Wrap each element's inline content in a span so its text can be measured and
    // width-constrained independently of the ::after arrow. Marked with a dataset
    // flag so re-scans (dynamic content) wrap each element exactly once.
    const wrap = (item: HTMLElement): void => {
        const label = document.createElement('span')
        label.className = 'hug-arrow__label'
        while (item.firstChild) label.appendChild(item.firstChild)
        item.appendChild(label)
        item.dataset.hugArrow = ''
    }

    const scan = (): void => {
        root.querySelectorAll<HTMLElement>(selector).forEach((item) => {
            if (item.dataset.hugArrow === undefined) wrap(item)
        })
    }

    const refresh = (): void => {
        scan()
        const on = active()

        // Query fresh each time so nodes removed since the last run (e.g. a re-render
        // that replaces innerHTML) simply drop out instead of leaving stale refs.
        root.querySelectorAll<HTMLElement>(selector).forEach((item) => {
            const label = item.querySelector<HTMLElement>(':scope > .hug-arrow__label')
            if (!label) return

            if (!on) {
                label.style.width = ''
                item.style.justifyContent = ''
                return
            }

            // Reset first so the text wraps freely, then measure per-line boxes.
            label.style.width = ''
            const range = document.createRange()
            range.selectNodeContents(label)
            const rects = range.getClientRects()

            let widest = 0
            for (let i = 0; i < rects.length; i++) widest = Math.max(widest, rects[i].width)

            // widest === 0 while the element is hidden (display:none) — skip until visible.
            if (widest > 0) {
                label.style.width = `${Math.ceil(widest)}px`
                item.style.justifyContent = 'flex-start'
            }
        })
    }

    let raf = 0
    const schedule = (): void => {
        if (raf) cancelAnimationFrame(raf)
        raf = requestAnimationFrame(refresh)
    }

    window.addEventListener('resize', schedule, { passive: true })

    // Re-run when nodes are added/removed under the root so client-rendered lists
    // (fetched cards, "load more") get picked up. Our own wrapping mutates the DOM
    // too, but scan() only wraps unmarked elements, so this can't loop.
    const observerTarget = (root === document ? document.body : root) as Node
    const observer = new MutationObserver(schedule)
    observer.observe(observerTarget, { childList: true, subtree: true })

    // Web fonts can change line wrapping after first paint — re-measure once ready.
    document.fonts?.ready.then(refresh)
    refresh()

    return {
        refresh,
        destroy() {
            window.removeEventListener('resize', schedule)
            observer.disconnect()
            if (raf) cancelAnimationFrame(raf)
        },
    }
}
